/**
 * OcrEngine — the ONE door to text recognition for every tool on the site.
 *
 *   const eng = await OcrEngine.open('rus+eng', { onProgress });
 *   const data = await eng.recognize(canvasOrBitmapOrBlob, { orient: true });
 *   // data: { text, confidence, lines, words, engine, rotation }  — the shape the
 *   //        Tesseract.js callers already consume, so a consumer changes one line.
 *   eng.close();
 *
 * WHY. Until 2026-09-12 nine tools called Tesseract.js directly while six others
 * had moved to the PP-OCRv5 lane (public/js/ocr-neural/) that this repository
 * measured 7× more accurate on its own ground-truth corpus (tests/ocr: 1.5 % vs
 * 11 % CER, and on the real-document corpus added the same day: a phone photo of
 * a Russian contract 2.3 % vs 26 %, a dark UI screenshot 0 % vs 100 %). Every
 * direct caller also carried the same latent defect — Tesseract turns a canvas
 * into a Blob INTERNALLY via canvas.toBlob(), which hands back null on a
 * memory-starved phone and dies inside the engine (Sentry D3, TIMBRICA-FRONTEND-D3
 * ×3 tools). The neural lane takes an ImageBitmap and never converts anything.
 *
 * WHAT IT DECIDES. Which engine serves a language: the neural recogniser knows
 * eight scripts (Latin, Cyrillic, Chinese/Japanese, Korean, Arabic, Greek,
 * Devanagari, Thai — every locale this site ships); a request outside them
 * (Hebrew, Georgian, Vietnamese diacritics, vertical CJK, two non-Latin scripts
 * at once) goes to Tesseract exactly as before. Neural init that fails (model
 * download, ORT) falls back to Tesseract too, with telemetry — a downgrade is a
 * fact to know, never a silent one.
 *
 * WHAT IT DOES NOT DO. No binarisation, no PSM: those were Tesseract's needs
 * (measured: Sauvola erased a dark UI screenshot entirely — 100 % CER — while the
 * neural engine read it clean). Orientation is asked of the engine itself: a
 * quick read of a small copy at 0°, and the other three quarter turns only when
 * that read looks wrong — the same «confidence decides» rule the etalon used
 * with a sparse Tesseract worker, minus the second engine.
 */
(function (root) {
    'use strict';
    if (root.OcrEngine) return;

    // ── language → neural script ─────────────────────────────────────────
    // Tesseract 3-letter codes AND BCP-47 tags. A language missing here is not
    // "unsupported" — it is served by Tesseract. Lists are conservative: a
    // language is listed only when its letters are in the recogniser's
    // dictionary (Kazakh Ә/Ң/Ұ, Vietnamese ơ/ư/ạ are not, so they stay out).
    var SCRIPT_OF = {
        eslav: ['rus', 'ukr', 'bel', 'bul', 'srp', 'mkd', 'ru', 'uk', 'be', 'bg', 'sr', 'mk'],
        latin: ['eng', 'spa', 'fra', 'deu', 'por', 'ita', 'tur', 'pol', 'nld', 'ind', 'ces', 'slk', 'hun', 'ron',
                'swe', 'dan', 'nor', 'fin', 'hrv', 'slv', 'cat', 'est', 'lav', 'lit', 'isl', 'eus', 'glg', 'gle',
                'cym', 'epo', 'lat', 'afr', 'bos', 'sqi', 'swa', 'tgl', 'uzb', 'msa', 'ltz', 'oci', 'aze',
                'en', 'es', 'fr', 'de', 'pt', 'it', 'tr', 'pl', 'nl', 'id', 'cs', 'sk', 'hu', 'ro', 'sv', 'da',
                'no', 'fi', 'hr', 'sl', 'ca', 'et', 'lv', 'lt', 'is', 'eu', 'gl', 'ga', 'cy', 'eo', 'la', 'af',
                'bs', 'sq', 'sw', 'tl', 'uz', 'ms', 'lb', 'oc', 'az'],
        ch: ['chi_sim', 'chi_tra', 'jpn', 'zh', 'zh-CN', 'zh-TW', 'ja'],
        korean: ['kor', 'ko'],
        arabic: ['ara', 'fas', 'urd', 'ar', 'fa', 'ur'],
        greek: ['ell', 'el'],
        devanagari: ['hin', 'mar', 'nep', 'san', 'hi', 'mr', 'ne'],
        thai: ['tha', 'th'],
    };
    var LANG_TO_SCRIPT = {};
    Object.keys(SCRIPT_OF).forEach(function (s) { SCRIPT_OF[s].forEach(function (l) { LANG_TO_SCRIPT[l] = s; }); });
    /** The language PPOCR.open() understands for a script (it maps BCP-47 → script itself). */
    var SCRIPT_LANG = { eslav: 'ru', latin: 'en', ch: 'zh', korean: 'ko', arabic: 'ar', greek: 'el', devanagari: 'hi', thai: 'th' };

    function langList(langs) {
        if (Array.isArray(langs)) return langs.slice();
        return String(langs || 'eng').split('+').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    /**
     * One neural script for a language set, or null when Tesseract must serve
     * it. English (and any other Latin-script language) rides along with a
     * non-Latin script: every recogniser dictionary carries the Latin alphabet
     * and digits, which is how a Russian contract with «Wi-Fi» and «LLC» reads.
     */
    function scriptFor(langs) {
        var list = langList(langs);
        var scripts = {};
        for (var i = 0; i < list.length; i++) {
            var s = LANG_TO_SCRIPT[list[i]] || LANG_TO_SCRIPT[String(list[i]).split(/[-_]/)[0]];
            if (!s) return null;
            scripts[s] = 1;
        }
        var keys = Object.keys(scripts);
        if (!keys.length) return null;
        if (keys.length === 1) return keys[0];
        var nonLatin = keys.filter(function (k) { return k !== 'latin'; });
        return nonLatin.length === 1 ? nonLatin[0] : null;
    }

    function ae(ev, d) { try { if (root.__ae) root.__ae(ev, d); } catch (_) { } }

    // ── sources → ImageBitmap / canvas ───────────────────────────────────
    function isCanvas(src) {
        return (typeof HTMLCanvasElement !== 'undefined' && src instanceof HTMLCanvasElement)
            || (typeof OffscreenCanvas !== 'undefined' && src instanceof OffscreenCanvas);
    }
    /** Anything the tools hold → a canvas we own (never the caller's). */
    async function toCanvas(src) {
        var c = document.createElement('canvas');
        if (isCanvas(src)) {
            c.width = src.width; c.height = src.height;
            c.getContext('2d').drawImage(src, 0, 0);
            return c;
        }
        if (typeof ImageData !== 'undefined' && src instanceof ImageData) {
            c.width = src.width; c.height = src.height;
            c.getContext('2d').putImageData(src, 0, 0);
            return c;
        }
        var bmp = (typeof ImageBitmap !== 'undefined' && src instanceof ImageBitmap) ? src : await createImageBitmap(src);
        c.width = bmp.width; c.height = bmp.height;
        c.getContext('2d').drawImage(bmp, 0, 0);
        if (bmp !== src) { try { bmp.close(); } catch (_) { } }
        return c;
    }
    function rotateQuarter(src, deg) {
        var c = document.createElement('canvas');
        if (deg === 180) { c.width = src.width; c.height = src.height; }
        else { c.width = src.height; c.height = src.width; }
        var ctx = c.getContext('2d');
        ctx.translate(c.width / 2, c.height / 2);
        ctx.rotate(deg * Math.PI / 180);
        ctx.drawImage(src, -src.width / 2, -src.height / 2);
        return c;
    }
    function downscale(src, maxDim) {
        var w = src.width, h = src.height;
        if (Math.max(w, h) <= maxDim) return src;
        var k = maxDim / Math.max(w, h);
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
        c.getContext('2d').drawImage(src, 0, 0, c.width, c.height);
        return c;
    }
    function release(c) { if (c && c.width) { c.width = c.height = 1; } }
    /**
     * Rotate a canvas by `deg` (counter-clockwise for positive values on a
     * y-down canvas, i.e. the correction for lines that fall to the right by
     * `deg`), on an enlarged canvas so no corner is lost. The fill is the
     * page's own margin colour — the median of its border — so a photo on a
     * grey desk stays a photo on a grey desk instead of gaining white wedges
     * the detector reads as edges.
     */
    function rotateCanvas(src, deg) {
        var rad = deg * Math.PI / 180;
        var cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
        var nw = Math.ceil(src.width * cos + src.height * sin);
        var nh = Math.ceil(src.width * sin + src.height * cos);
        var c = document.createElement('canvas');
        c.width = nw; c.height = nh;
        var ctx = c.getContext('2d');
        ctx.fillStyle = borderColor(src);
        ctx.fillRect(0, 0, nw, nh);
        ctx.translate(nw / 2, nh / 2);
        ctx.rotate(-rad);
        ctx.drawImage(src, -src.width / 2, -src.height / 2);
        return c;
    }
    function borderColor(src) {
        try {
            var ctx = src.getContext('2d', { willReadFrequently: true });
            var w = src.width, h = src.height, vals = [];
            var take = function (d) { for (var i = 0; i < d.length; i += 16) vals.push((d[i] + d[i + 1] + d[i + 2]) / 3); };
            take(ctx.getImageData(0, 0, w, 1).data); take(ctx.getImageData(0, h - 1, w, 1).data);
            take(ctx.getImageData(0, 0, 1, h).data); take(ctx.getImageData(w - 1, 0, 1, h).data);
            vals.sort(function (a, b) { return a - b; });
            var v = Math.round(vals[vals.length >> 1] || 255);
            return 'rgb(' + v + ',' + v + ',' + v + ')';
        } catch (_) { return '#fff'; }
    }

    // ── neural result → the Tesseract-shaped data the tools consume ──────
    /**
     * lines come in reading order with a row index; words are cut from a line
     * by whitespace and given a share of the line box proportional to their
     * characters — Paddle recognises lines, not words, and the consumers that
     * draw word boxes (etalon workspace, searchable PDF) need boxes that sit on
     * the right word, not exact glyph bounds. Confidence is 0–100 like Tesseract.
     */
    function toTesseractLike(res, meta) {
        var src = (res && res.lines) || [];
        var rows = [];
        src.forEach(function (l) {
            var r = l.row == null ? rows.length : l.row;
            (rows[r] = rows[r] || []).push(l);
        });
        rows = rows.filter(Boolean);
        var lines = [], words = [], text = '', confSum = 0, confN = 0;
        // A tilted line's box is taller than the line (its bounds include the
        // drift); the line's own height is the quad's short side.
        function lineHeight(l) {
            if (l.quad && l.quad.length === 4) { var p = l.quad[0], q = l.quad[3]; return Math.hypot(q[0] - p[0], q[1] - p[1]); }
            return l.box ? l.box.h : 0;
        }
        var heights = src.map(lineHeight).filter(function (h) { return h > 0; }).sort(function (a, b) { return a - b; });
        var medH = heights.length ? heights[heights.length >> 1] : 0;
        var prevBottom = null;
        rows.forEach(function (row) {
            row.sort(function (a, b) { return (a.box ? a.box.x : 0) - (b.box ? b.box.x : 0); });
            // A right-to-left row is READ from its rightmost box: the Arabic
            // recogniser splits a phrase into boxes like any other, and joined left
            // to right «مخرج الطوارئ» came out as «الطوارئ مخرج».
            var rowRtl = (row.map(function (l) { return l.text || ''; }).join(' ').match(/[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\uFB1D-\uFDFF\uFE70-\uFEFF]/g) || []).length;
            var rowLetters = (row.map(function (l) { return l.text || ''; }).join(' ').match(/\p{L}/gu) || []).length;
            if (rowRtl > 0 && rowRtl >= 0.5 * rowLetters) row.reverse();
            var top = Math.min.apply(null, row.map(function (l) { return l.box ? l.box.y : 0; }));
            if (prevBottom != null && medH > 0 && top - prevBottom > 1.6 * medH) text += '\n';   // paragraph gap
            var rowText = [];
            row.forEach(function (l) {
                var t = String(l.text || '').trim();
                if (!t) return;
                var b = l.box || { x: 0, y: 0, w: 0, h: 0 };
                var conf = Math.round(Math.max(0, Math.min(1, l.conf == null ? 0.9 : l.conf)) * 100);
                var bbox = { x0: b.x, y0: b.y, x1: b.x + b.w, y1: b.y + b.h };
                var lineWords = [];
                var quad = l.quad && l.quad.length === 4 && l.angle ? l.quad : null;
                // A word's extent along the line: from the recogniser's own
                // alignment when the engine sends it, else cut by character count.
                var spans = (l.words && l.words.length) ? l.words : (function () {
                    var toks = t.split(/\s+/).filter(Boolean), out = [], cursor = 0;
                    var chars = toks.reduce(function (s, w) { return s + w.length; }, 0) + Math.max(0, toks.length - 1);
                    toks.forEach(function (w) { out.push({ text: w, f0: chars ? cursor / chars : 0, f1: chars ? (cursor + w.length) / chars : 1 }); cursor += w.length + 1; });
                    return out;
                })();
                spans.forEach(function (sp) {
                    var w = sp.text, t0 = sp.f0, t1 = sp.f1, wb;
                    if (quad) {
                        // a slice of the tilted line along its own top and bottom edges
                        var pts = [0, 1].map(function (k) { var a = quad[k ? 3 : 0], c = quad[k ? 2 : 1]; return [[a[0] + (c[0] - a[0]) * t0, a[1] + (c[1] - a[1]) * t0], [a[0] + (c[0] - a[0]) * t1, a[1] + (c[1] - a[1]) * t1]]; });
                        var xs = [pts[0][0][0], pts[0][1][0], pts[1][0][0], pts[1][1][0]], ys = [pts[0][0][1], pts[0][1][1], pts[1][0][1], pts[1][1][1]];
                        wb = { x0: Math.round(Math.min.apply(null, xs)), y0: Math.round(Math.min.apply(null, ys)), x1: Math.round(Math.max.apply(null, xs)), y1: Math.round(Math.max.apply(null, ys)) };
                    } else {
                        wb = { x0: Math.round(b.x + t0 * b.w), y0: b.y, x1: Math.round(b.x + t1 * b.w), y1: b.y + b.h };
                    }
                    var wd = { text: w, confidence: conf, bbox: wb };
                    lineWords.push(wd); words.push(wd);
                });
                lines.push({ text: t, confidence: conf, bbox: bbox, quad: l.quad || null, angle: l.angle || 0, words: lineWords, row: l.row });
                rowText.push(t);
                confSum += conf; confN++;
            });
            if (rowText.length) text += rowText.join(' ') + '\n';
            prevBottom = Math.max.apply(null, row.map(function (l) { return l.box ? l.box.y + l.box.h : 0; }));
        });
        return {
            text: text.replace(/\n{3,}/g, '\n\n').trim(),
            confidence: confN ? confSum / confN : 0,
            lines: lines,
            words: words,
            engine: 'neural',
            provider: meta && meta.provider,
            width: meta && meta.width, height: meta && meta.height,
            rotation: (meta && meta.rotation) || 0,
            msDet: res && res.msDet, msRec: res && res.msRec, msTotal: res && res.msTotal,
        };
    }

    // ── the neural engine ────────────────────────────────────────────────
    async function openNeural(script, opts) {
        var lang = SCRIPT_LANG[script];
        var eng = await root.PPOCR.open(lang, {
            onProgress: opts.onProgress,
            workerUrl: opts.workerUrl || root.__PPOCR_WORKER_URL || undefined,
            webgpu: opts.webgpu,
        });
        var busy = Promise.resolve();
        /** Reads a canvas the engine may consume (a copy is made for the bitmap). */
        async function readCanvas(canvas, cfg) {
            var bmp = await createImageBitmap(canvas);
            var res = await eng.read(bmp, { cfg: cfg || {} });   // read() transfers and closes the bitmap
            return res;
        }
        /**
         * Which quarter turn reads best. 0° first on a ≤800 px copy; the other
         * turns only when 0° looks wrong (little text or low confidence), and a
         * turn wins only by a clear margin — never rotate on noise. Same rule the
         * etalon applied with a sparse Tesseract worker.
         */
        async function probeOrientation(canvas) {
            var small = downscale(canvas, 800);
            var run = async function (deg) {
                var c = deg === 0 ? small : rotateQuarter(small, deg);
                var out = { deg: deg, score: 0, chars: 0, conf: 0 };
                try {
                    var r = await readCanvas(c);
                    var chars = 0, conf = 0, n = 0;
                    (r.lines || []).forEach(function (l) { chars += String(l.text || '').replace(/\s/g, '').length; conf += l.conf || 0; n++; });
                    out.chars = chars; out.conf = n ? conf / n : 0;
                    out.score = Math.round(chars * out.conf * 100) / 100;
                } catch (_) { }
                if (c !== small) release(c);
                return out;
            };
            var r0 = await run(0);
            var best = r0;
            // «Looks wrong» = fewer than 20 characters or a mean confidence under
            // 0.6 — a page read sideways yields exactly that: a few stray
            // characters, none of them sure. Only then are the other three turns
            // paid for.
            if (!(r0.chars >= 20 && r0.conf >= 0.6)) {
                var turns = [90, 180, 270];
                for (var i = 0; i < turns.length; i++) {
                    var rd = await run(turns[i]);
                    if (rd.score > best.score) best = rd;
                }
            }
            if (small !== canvas) release(small);
            // A turn wins only by a clear margin — never rotate on noise.
            if (best.deg !== 0 && best.score > r0.score * 1.3) {
                ae('feature_use', { f: 'ocr_auto_rotate', deg: best.deg, s0: r0.score, sbest: best.score });
                return best.deg;
            }
            return 0;
        }
        return {
            kind: 'neural', script: script, provider: eng.provider,
            /** Which quarter turn reads best (0/90/180/270) — the caller rotates. */
            probeOrientation: function (source) {
                var task = busy.then(async function () {
                    var own = !isCanvas(source);
                    var canvas = own ? await toCanvas(source) : source;
                    var deg = await probeOrientation(canvas);
                    if (own) release(canvas);
                    return deg;
                });
                busy = task.catch(function () { });
                return task;
            },
            /**
             * @param source  canvas | OffscreenCanvas | ImageBitmap | Blob | ImageData
             * @param o.orient  ask the engine which quarter turn reads best first
             *                  (fine tilt needs no step here: the worker reads
             *                  every line through its own tilted rectangle)
             * @param o.onLine  per-line progress ({index,total})
             */
            recognize: function (source, o) {
                o = o || {};
                var task = busy.then(async function () {
                    var canvas = await toCanvas(source);
                    var rotation = 0;
                    if (o.orient) {
                        rotation = await probeOrientation(canvas);
                        if (rotation) { var rc = rotateQuarter(canvas, rotation); release(canvas); canvas = rc; }
                    }
                    var W = canvas.width, H = canvas.height;
                    var bmp = await createImageBitmap(canvas);
                    var res = await eng.read(bmp, { cfg: o.cfg || {}, onLine: o.onLine });
                    release(canvas);
                    return toTesseractLike(res, { provider: eng.provider, width: W, height: H, rotation: rotation });
                });
                busy = task.catch(function () { });
                return task;
            },
            close: function () { try { eng.close(); } catch (_) { } },
        };
    }

    // ── the Tesseract engine (unchanged behaviour, one owner) ─────────────
    /**
     * Any failure value → an Error with a code. tesseract.js rejects its load
     * with the worker ErrorEvent's `message`, and WebKit fires a plain Event
     * for a worker script that never arrived — so a Tesseract that could not
     * start rejected with `undefined`, and the tool's own catch died on
     * `err.message` (Sentry TIMBRICA-FRONTEND-JZ, iPhone: the neural models
     * had failed to download first, then the fallback lane failed the same
     * way, and the person saw a crash instead of «engine did not load»).
     * The engine that could not open is a typed, expected refusal here.
     */
    function engineError(code, cause) {
        var msg = '';
        if (cause && typeof cause === 'object') msg = cause.message || (cause.type ? 'event:' + cause.type : '');
        else if (cause != null) msg = String(cause);
        var e = new Error(code + (msg ? ': ' + msg : ''));
        e.code = code; e.expected = true; e.cause = cause;
        return e;
    }
    async function loadTesseract(opts) {
        if (root.Tesseract) return root.Tesseract;
        var urls = [opts.tesseractUrl || root.__OCR_SCRIPT, opts.tesseractFallbackUrl || root.__OCR_SCRIPT_FALLBACK].filter(Boolean);
        if (!urls.length) throw engineError('tesseract_missing');
        for (var i = 0; i < urls.length; i++) {
            try {
                await new Promise(function (resolve, reject) {
                    var s = document.createElement('script');
                    s.src = urls[i]; s.async = true;
                    s.onload = resolve; s.onerror = function () { reject(new Error('load failed')); };
                    document.head.appendChild(s);
                });
                if (root.Tesseract) return root.Tesseract;
            } catch (_) { }
        }
        throw engineError('tesseract_missing', 'none of ' + urls.length + ' sources loaded');
    }
    async function openTesseract(langs, opts) {
        var T = await loadTesseract(opts);
        var wo = {};
        var t = opts.tesseract || {};
        if (t.langPath || root.__OCR_LANGPATH) wo.langPath = t.langPath || root.__OCR_LANGPATH;
        if (t.workerPath || root.__OCR_WORKERPATH) wo.workerPath = t.workerPath || root.__OCR_WORKERPATH;
        if (t.corePath || root.__OCR_COREPATH) wo.corePath = t.corePath || root.__OCR_COREPATH;
        wo.errorHandler = function (err) { try { console.warn('[ocr] tesseract worker error:', err); } catch (_) { } };
        if (opts.onProgress) wo.logger = function (m) {
            if (m && typeof m.progress === 'number' && /loading/.test(m.status || '')) opts.onProgress(Math.max(0, Math.min(1, m.progress)));
        };
        var w;
        try {
            w = await T.createWorker(langList(langs).join('+'), 1, wo);
            var psm = (opts.tesseract && opts.tesseract.psm) || '3';   // full layout analysis, never the v5 default 6
            await w.setParameters({ tessedit_pageseg_mode: psm });
        } catch (e) {
            if (w) { try { w.terminate(); } catch (_) { } }
            throw engineError('tesseract_worker_failed', e);
        }
        if (opts.onProgress) opts.onProgress(1);
        var busy = Promise.resolve();
        async function recognizeCanvas(canvas) {
            // The engine would call toBlob itself and die on a null; we make the
            // Blob where null is checkable (Sentry D3).
            var blob = await new Promise(function (resolve) { try { canvas.toBlob(resolve, 'image/png'); } catch (_) { resolve(null); } });
            if (!blob || !blob.size) {
                var e = new Error('canvas.toBlob returned null at ' + canvas.width + '×' + canvas.height);
                e.code = 'canvas_to_blob_null'; e.expected = true;
                throw e;
            }
            var r = await w.recognize(blob, {}, { text: true, blocks: true });
            return (r && r.data) || {};
        }
        /** Same rule as the etalon's sparse probe: words × mean confidence, a turn wins only by a clear margin. */
        async function probeOrientation(canvas) {
            var small = downscale(canvas, 800);
            var scores = {};
            for (var i = 0; i < 4; i++) {
                var deg = [0, 90, 180, 270][i];
                var c = deg === 0 ? small : rotateQuarter(small, deg);
                try {
                    var d = await recognizeCanvas(c);
                    var words = (d.words || []).filter(function (x) { return x.text && x.text.trim().length > 1 && (x.confidence || 0) > 40; });
                    var mean = words.length ? words.reduce(function (a, x) { return a + x.confidence; }, 0) / words.length : 0;
                    scores[deg] = Math.round(words.length * mean);
                } catch (_) { scores[deg] = 0; }
                if (c !== small) release(c);
                // 0° reads well → the other turns are not paid for.
                if (deg === 0 && scores[0] > 0 && words && words.length >= 8 && mean >= 60) break;
            }
            if (small !== canvas) release(small);
            var best = 0;
            [90, 180, 270].forEach(function (deg) { if ((scores[deg] || 0) > (scores[best] || 0)) best = deg; });
            return (best !== 0 && scores[best] > (scores[0] || 0) * 1.3) ? best : 0;
        }
        return {
            kind: 'tesseract', script: null, provider: 'wasm',
            probeOrientation: function (source) {
                var task = busy.then(async function () {
                    var own = !isCanvas(source);
                    var canvas = own ? await toCanvas(source) : source;
                    var deg = await probeOrientation(canvas);
                    if (own) release(canvas);
                    return deg;
                });
                busy = task.catch(function () { });
                return task;
            },
            recognize: function (source, o) {
                o = o || {};
                var task = busy.then(async function () {
                    var canvas = await toCanvas(source);
                    var W = canvas.width, H = canvas.height;
                    var d;
                    try { d = await recognizeCanvas(canvas); } finally { release(canvas); }
                    return {
                        text: d.text || '', confidence: d.confidence || 0,
                        lines: (d.lines || []).map(function (l) { return { text: l.text, confidence: l.confidence, bbox: l.bbox, words: l.words || [] }; }),
                        words: (d.words || []).map(function (x) { return { text: x.text, confidence: x.confidence, bbox: x.bbox }; }),
                        engine: 'tesseract', provider: 'wasm', width: W, height: H, rotation: 0,
                    };
                });
                busy = task.catch(function () { });
                return task;
            },
            setParameters: function (p) { return w.setParameters(p); },
            close: function () { try { w.terminate(); } catch (_) { } },
        };
    }

    // ── the door ─────────────────────────────────────────────────────────
    /**
     * open(langs, opts) → engine. langs: 'rus+eng' | ['rus','eng'] | 'ru'.
     * opts: onProgress(0..1), workerUrl, prefer ('neural' default | 'tesseract'),
     *       tesseract {workerPath, corePath, langPath, psm}, slug (telemetry).
     */
    async function open(langs, opts) {
        opts = opts || {};
        var slug = opts.slug || root.__TOOL_SLUG || '';
        var script = scriptFor(langs);
        var wantNeural = opts.prefer !== 'tesseract' && !!script && !!root.PPOCR;
        if (wantNeural) {
            try {
                var e = await openNeural(script, opts);
                ae('feature_use', { slug: slug, f: 'ocr_engine', engine: 'neural', script: script, provider: e.provider });
                return e;
            } catch (err) {
                // A downgrade is a fact to know: the neural lane failed on THIS
                // device (download, ORT init) and Tesseract takes the file.
                ae('tool_diagnostic', { slug: slug, issue: 'ocr_neural_fallback', script: script, msg: String((err && err.message) || err).slice(0, 120) });
            }
        }
        var t;
        try {
            t = await openTesseract(langs, opts);
        } catch (e) {
            var err = (e && e.code) ? e : engineError('tesseract_unavailable', e);
            // Both lanes down (or the only lane): a network/CDN fact on THIS
            // device, reported as such — the tool shows its «engine did not
            // load» line off err.expected, never a generic batch failure.
            ae('tool_diagnostic', { slug: slug, issue: 'ocr_engine_unavailable', code: err.code, neural: wantNeural ? 'failed' : 'none', msg: String(err.message || '').slice(0, 120) });
            throw err;
        }
        ae('feature_use', { slug: slug, f: 'ocr_engine', engine: 'tesseract', script: script || 'none', why: wantNeural ? 'neural_failed' : (root.PPOCR ? 'script' : 'no_neural') });
        return t;
    }

    root.OcrEngine = {
        open: open,
        scriptFor: scriptFor,
        rotateCanvas: rotateCanvas,
        supports: function (langs) { return !!scriptFor(langs); },
        toTesseractLike: toTesseractLike,
        /** Bytes the neural models cost before the first read (0 when cached). */
        downloadMb: async function (langs) {
            var s = scriptFor(langs);
            if (!s || !root.PPOCR) return 0;
            var lang = SCRIPT_LANG[s];
            try { return (await root.PPOCR.isCached(lang)) ? 0 : root.PPOCR.downloadMb(lang, false); } catch (_) { return 0; }
        },
        _langList: langList,
    };
})(window);
